import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { accessNoteSchema } from '../utils/validation';
import { accessNote } from '../services/notes';
import { checkRateLimit } from '../services/rateLimit';
import { verifyReCaptcha } from '../services/recaptcha';
import useReCaptcha from './useReCaptcha';
import { useReCaptchaContext } from '../contexts/ReCaptchaContext';
import type { Note } from '../types';

interface UseNoteAccessProps {
  note: Note | null;
  id: string | undefined;
  onError?: (error: Error) => void;
}

interface UseNoteAccessReturn {
  password: string;
  setPassword: (password: string) => void;
  content: string;
  showContent: boolean;
  setShowContent: (show: boolean) => void;
  toast: { message: string; type: 'error' | 'success' | 'info' } | null;
  setToast: (toast: { message: string; type: 'error' | 'success' | 'info' } | null) => void;
  handleAccess: (e?: React.FormEvent) => Promise<void>;
  copyContent: () => Promise<void>;
  isDecrypting: boolean;
}

export default function useNoteAccess({ note, id, onError }: UseNoteAccessProps): UseNoteAccessReturn {
  const { t } = useTranslation('viewNote');
  const { getReCaptchaToken } = useReCaptcha();
  const { isEnabled: recaptchaEnabled } = useReCaptchaContext();
  const [password, setPassword] = useState('');
  const [content, setContent] = useState('');
  const [showContent, setShowContent] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' | 'info' } | null>(null);

  const handleAccess = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!note || !id || isDecrypting) {
      return;
    }

    setToast(null);
    setIsDecrypting(true);

    try {
      let isVerified = true;
      if (recaptchaEnabled) {
        const token = await getReCaptchaToken('access_note');
        isVerified = await verifyReCaptcha(token, 'access_note');
      }

      if (!isVerified) {
        if (onError) {
          onError(new Error(t('errors.recaptcha')));
        }
        setIsDecrypting(false);
        return;
      }

      // Only validate password if note is encrypted
      if (note.encrypted) {
        try {
          await accessNoteSchema.parseAsync({ password });
        } catch (validationError) {
          if (onError) {
            onError(new Error(t('form.password.validation.tooShort', { min: 6 })));
          }
          setIsDecrypting(false);
          return;
        }
      }

      const result = await accessNote(id, note.encrypted ? password : '');
      
      if (result.error) {
        if (result.error.isInvalidPassword) {
          // Only check rate limit after a failed password attempt
          await checkRateLimit(id);
          if (onError) {
            onError(new Error(t('form.password.validation.invalid')));
          }
        } else {
          if (onError) {
            onError(new Error(result.error.message));
          }
        }
        setIsDecrypting(false);
        return;
      }

      if (!result.content) {
        if (onError) {
          onError(new Error(t('errors.access')));
        }
        setIsDecrypting(false);
        return;
      }
      
      const sanitizedContent = DOMPurify.sanitize(result.content, {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
      });
      
      setContent(sanitizedContent);
      setShowContent(true);
    } catch (err) {
      if (onError) {
        onError(err instanceof Error ? err : new Error(t('errors.access')));
      }
    } finally {
      setIsDecrypting(false);
    }
  }, [note, id, password, t, isDecrypting, onError, getReCaptchaToken, recaptchaEnabled]);

  const copyContent = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setToast({
        message: t('content.actions.copied'),
        type: 'success'
      });
    } catch (err) {
      setToast({
        message: t('content.actions.copyError'),
        type: 'error'
      });
    }
  };

  return {
    password,
    setPassword,
    content,
    showContent,
    setShowContent,
    toast,
    setToast,
    handleAccess,
    copyContent,
    isDecrypting
  };
}